/**
 * "Wat er is": een stille lijst met alleen wat er ís, met context. Pure functie.
 *
 * - Een teller op 0 verdwijnt (geen "0 quitclaims"); onbekend (null, nog aan het laden) ook.
 * - Per fase staan alleen de fasekritische regels in de lijst; al het andere dat er is komt
 *   in één regel "Naslag: 7 notities · 30 mails · …".
 * - Notities, documenten en meetings hebben één plek: óf een eigen regel, óf in de naslag.
 */
import { korteDatum, dagEnDatum } from './datums';
import {
  draaidagStand, takenOverDatum, deliverableStatus, deliverableNaam, geld,
} from './projectStand';
import { isTaakAfgerond } from '../../ProjectOverview/parts/projectMeta';


/** Fasekritische regels per fase, in volgorde. Onbekende fase → algemene set. */
export const KRITISCH_PER_FASE = {
  opstartfase: ['offerte', 'documenten', 'notes', 'meetings', 'correspondentie'],
  offertefase: ['offerte', 'deliverables', 'correspondentie', 'documenten'],
  concept: ['documenten', 'notes', 'meetings', 'deliverables'],
  preproductie: ['taken', 'deliverables', 'draaidagen', 'documenten'],
  productie: ['draaidagen', 'taken', 'deliverables', 'quitclaims'],
  postproductie: ['deliverables', 'taken', 'frameio', 'qa'],
  afronding: ['deliverables', 'taken', 'correspondentie'],
  evaluatie: ['correspondentie', 'documenten', 'meetings'],
};
const STANDAARD = ['taken', 'deliverables', 'draaidagen', 'documenten'];

/** Volgorde + woorden in de naslagregel. */
const NASLAG = [
  { key: 'notes', enkel: 'notitie', meer: 'notities' },
  { key: 'correspondentie', enkel: 'mail', meer: 'mails' },
  { key: 'meetings', enkel: 'meeting', meer: 'meetings' },
  { key: 'documenten', enkel: 'document', meer: 'documenten' },
  { key: 'callsheets', enkel: 'callsheet', meer: 'callsheets' },
  { key: 'taken', enkel: 'taak', meer: 'taken' },
  { key: 'deliverables', enkel: 'deliverable', meer: 'deliverables' },
  { key: 'draaidagen', enkel: 'draaidag', meer: 'draaidagen' },
  { key: 'quitclaims', enkel: 'quitclaim', meer: 'quitclaims' },
  { key: 'frameio', enkel: "video op Frame.io", meer: "video's op Frame.io" },
  { key: 'qa', enkel: 'QA-review', meer: 'QA-reviews' },
];

const LABELS = {
  offerte: 'Offerte', taken: 'Taken', deliverables: 'Deliverables', draaidagen: 'Draaidagen',
  quitclaims: 'Quitclaims', callsheets: 'Callsheets', notes: 'Notities', meetings: 'Meetings',
  documenten: 'Documenten', correspondentie: 'Mails', frameio: 'Frame.io', qa: 'QA-reviews',
};

const aantal = (x) => (Array.isArray(x) ? x.length : (typeof x === 'number' ? x : null));

function samenvattingDeliverables(lijst) {
  const per = new Map();
  lijst.forEach((d) => {
    const s = deliverableStatus(d) || 'Nog niet gestart';
    per.set(s, (per.get(s) || 0) + 1);
  });
  if (lijst.length <= 2) return lijst.map(deliverableNaam).join(', ');
  // Alleen de actuele voortgang; "nog niet gestart" alleen als er verder niets loopt.
  const stil = { 'In progress': 'in productie', Opgeleverd: 'opgeleverd' };
  const lopend = [...per.entries()].filter(([s]) => s !== 'Nog niet gestart');
  if (!lopend.length) return 'nog niet gestart';
  return lopend.map(([s, n]) => `${n} ${stil[s] || s.toLowerCase()}`).join(', ');
}

/**
 * @param {object} d
 *   fase (phase_key), vandaag, offerte (offerteStand), taken, deliverables, draaidagen,
 *   quitclaims, callsheets, notes, meetings (transcripts), documenten ({aantal, laatste}|null),
 *   mails (mailStand|null), frameio, qa
 * @returns {{ rijen: Array<{key,label,waarde,context,probleem?}>, naslag: Array<{key,label,aantal}> }}
 */
export function watErIs(d = {}) {
  const v = d.vandaag;
  const kandidaten = {};

  // Offerte: alleen zolang hij niet akkoord is (daarna staat hij in de zijkolom).
  if (d.offerte?.stand === 'open') {
    kandidaten.offerte = {
      waarde: d.offerte.bedrag ? `${geld(d.offerte.bedrag)} open` : 'open',
      context: '',   // de regels staan achter de klik (Offerteregels)
      aantal: 1,
    };
  }

  const taken = (d.taken || []).filter((t) => t?.fields?.PrioNr !== 'Gearchiveerd');
  if (taken.length) {
    const af = taken.filter(isTaakAfgerond).length;
    const laat = takenOverDatum(taken, v).length;
    kandidaten.taken = {
      waarde: `${af} van ${taken.length} klaar`,
      context: laat ? `${laat} over datum` : '',
      probleem: laat > 0,
      aantal: taken.length,
    };
  }

  const dels = d.deliverables || [];
  if (dels.length) {
    kandidaten.deliverables = { waarde: String(dels.length), context: samenvattingDeliverables(dels), aantal: dels.length };
  }

  const dd = draaidagStand(d.draaidagen, v);
  if (dd.geldig.length) {
    const delen = [];
    if (dd.verleden.length) delen.push(`${dd.verleden.length} geweest`);
    if (dd.komend.length) delen.push(`volgende ${dagEnDatum(dd.komend[0].dag, v)}`);
    kandidaten.draaidagen = { waarde: String(dd.geldig.length), context: delen.join(', '), aantal: dd.geldig.length };
  }

  const qc = d.quitclaims || [];
  if (qc.length) {
    const binnen = qc.filter((q) => /getekend|binnen|ontvangen|akkoord/i.test(String(q.fields?.Status || ''))).length;
    kandidaten.quitclaims = binnen
      ? { waarde: `${binnen} van ${qc.length} binnen`, context: '', aantal: qc.length }
      : { waarde: String(qc.length), context: '', aantal: qc.length };
  }

  const notes = d.notes || [];
  if (notes.length) {
    const n = notes[0]?.fields || {};
    const datum = n.Notitiedatum || n.Aanmaakdatum || n.Aangemaakt;
    kandidaten.notes = {
      waarde: String(notes.length),
      context: [datum ? `laatste ${korteDatum(datum, v)}` : '', n.Onderwerp || n.Titel || ''].filter(Boolean).join(': '),
      aantal: notes.length,
    };
  }

  const meetings = d.meetings || [];
  if (meetings.length) kandidaten.meetings = { waarde: String(meetings.length), context: '', aantal: meetings.length };

  const docN = aantal(d.documenten?.aantal ?? null);
  if (docN) {
    kandidaten.documenten = { waarde: String(docN), context: d.documenten?.laatste || '', aantal: docN };
  }

  if (d.mails?.aantal) {
    const wie = d.mails.laatsteVanKlant === true ? ', van de klant' : d.mails.laatsteVanKlant === false ? ', van ons' : '';
    kandidaten.correspondentie = {
      waarde: String(d.mails.aantal),
      context: d.mails.laatsteDatum ? `laatste ${korteDatum(d.mails.laatsteDatum, v)}${wie}` : '',
      aantal: d.mails.aantal,
    };
  }

  const cs = aantal(d.callsheets);
  if (cs) kandidaten.callsheets = { waarde: String(cs), context: '', aantal: cs };
  const fio = aantal(d.frameio);
  if (fio) kandidaten.frameio = { waarde: String(fio), context: "video's", aantal: fio };
  const qa = aantal(d.qa);
  if (qa) kandidaten.qa = { waarde: String(qa), context: '', aantal: qa };

  // Een offerte die nog open staat is in elke fase kritisch (anders valt hij nergens op).
  const basis = KRITISCH_PER_FASE[d.fase] || STANDAARD;
  const kritisch = basis.includes('offerte') ? basis : ['offerte', ...basis];
  const rijen = kritisch.filter((k) => kandidaten[k]).map((k) => ({ key: k, label: LABELS[k], ...kandidaten[k] }));
  const inRij = new Set(rijen.map((r) => r.key));
  const naslag = NASLAG.filter(({ key }) => kandidaten[key] && !inRij.has(key))
    .map(({ key, enkel, meer }) => ({ key, aantal: kandidaten[key].aantal, label: kandidaten[key].aantal === 1 ? enkel : meer }));
  return { rijen, naslag };
}
